/* eslint-disable no-param-reassign, hh/comment-budget */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { VIEW_TYPES } from 'components/common/view-type/settings'
import { CONTAINERS_TABS } from 'containers/inventory/settings'
import { DATA_GRID_DEFAULT_PINNED_COLUMNS } from 'settings/constants/dataGrid'
import { get } from 'utils/lodash'

import type { GridPinnedColumnFields, GridSortItem, GridSortModel } from '@mui/x-data-grid-pro'
import type { ContainerRowKind } from 'components/inventory/containers/rowKinds'

const { GRID } = VIEW_TYPES

export type ContainersViewType = 'grid' | 'list'

export type ContainersTab = 'List' | 'Map' | 'Activity'

// Keyed by getContainersViewKey, so each status and each yard keeps its own sort and open tab.
export type PersistentInventoryContainersState = {
  dataGridViewType: ContainersViewType
  selectedTab: ContainersTab
  pinnedColumns: GridPinnedColumnFields
  columnOrder: string[]
  // GridSortModel is readonly, which Immer drafts reject — store a mutable copy.
  sortModelByView: Record<string, GridSortItem[]>
  rowKindByView: Record<string, ContainerRowKind>
  // Opaque tab id, empty until the operator picks one. The feed owns which tabs exist and
  // resolves an unavailable id back to its default.
  activityTab: string
  showEmptyYards: boolean
}

const initialState: PersistentInventoryContainersState = {
  dataGridViewType: GRID as ContainersViewType,
  selectedTab: CONTAINERS_TABS.LIST as ContainersTab,
  pinnedColumns: { ...DATA_GRID_DEFAULT_PINNED_COLUMNS },
  columnOrder: [],
  sortModelByView: {},
  rowKindByView: {},
  activityTab: '',
  showEmptyYards: false,
}

const inventoryContainersSlice = createSlice({
  name: 'persistentInventoryContainers',
  initialState: { ...initialState },
  reducers: {
    setViewType: (state, action: PayloadAction<ContainersViewType>) => {
      state.dataGridViewType = action.payload
    },
    setSelectedTab: (state, action: PayloadAction<ContainersTab>) => {
      state.selectedTab = action.payload
    },
    setPinnedColumns: (state, action: PayloadAction<GridPinnedColumnFields>) => {
      state.pinnedColumns = action.payload
    },
    setColumnOrder: (state, action: PayloadAction<string[]>) => {
      state.columnOrder = action.payload
    },
    setSortModel: (state, action: PayloadAction<{ view: string; sortModel: GridSortModel }>) => {
      const { view, sortModel } = action.payload
      // Sessions persisted before sortModelByView existed rehydrate without it.
      if (!state.sortModelByView) state.sortModelByView = {}
      state.sortModelByView[view] = [...sortModel]
    },
    setRowKind: (state, action: PayloadAction<{ view: string; rowKind: ContainerRowKind }>) => {
      const { view, rowKind } = action.payload
      // Sessions persisted before rowKindByView existed rehydrate without it.
      if (!state.rowKindByView) state.rowKindByView = {}
      state.rowKindByView[view] = rowKind
    },
    setActivityTab: (state, action: PayloadAction<string>) => {
      state.activityTab = action.payload
    },
    setShowEmptyYards: (state, action: PayloadAction<boolean>) => {
      state.showEmptyYards = action.payload
    },
  },
})

export const {
  setViewType,
  setSelectedTab,
  setPinnedColumns,
  setColumnOrder,
  setSortModel,
  setRowKind,
  setActivityTab,
  setShowEmptyYards,
} = inventoryContainersSlice.actions

export const selectViewType = (state: unknown): ContainersViewType =>
  (get(state, 'persistentInventoryContainers.dataGridViewType') as ContainersViewType) ?? initialState.dataGridViewType

export const selectSelectedTab = (state: unknown): ContainersTab =>
  (get(state, 'persistentInventoryContainers.selectedTab') as ContainersTab) ?? initialState.selectedTab

export const selectPinnedColumns = (state: unknown): GridPinnedColumnFields =>
  (get(state, 'persistentInventoryContainers.pinnedColumns') as GridPinnedColumnFields) ?? initialState.pinnedColumns

export const selectColumnOrder = (state: unknown): string[] =>
  (get(state, 'persistentInventoryContainers.columnOrder') as string[]) ?? initialState.columnOrder

export const selectSortModelByView = (state: unknown): Record<string, GridSortItem[]> =>
  (get(state, 'persistentInventoryContainers.sortModelByView') as Record<string, GridSortItem[]>) ?? initialState.sortModelByView

export const selectRowKindByView = (state: unknown): Record<string, ContainerRowKind> =>
  (get(state, 'persistentInventoryContainers.rowKindByView') as Record<string, ContainerRowKind>) ?? initialState.rowKindByView

export const selectActivityTab = (state: unknown): string =>
  (get(state, 'persistentInventoryContainers.activityTab') as string) ?? initialState.activityTab

export const selectShowEmptyYards = (state: unknown): boolean =>
  (get(state, 'persistentInventoryContainers.showEmptyYards') as boolean) ?? initialState.showEmptyYards

export default inventoryContainersSlice.reducer
